import React, { useEffect, useState } from "react";
import get from "lodash/get";
import { connect, useDispatch } from "react-redux";
import { compose } from "redux";
import { withApollo } from "react-apollo";
import { Auth } from "aws-amplify";
import { loadUserInfo } from "../user/redux/actions";
import styled from "styled-components";
import { signOut } from "../../utils/signout";
import { react18WorkaroundProps } from "../../utils/constants/react18WorkaroundProps";
import {
  GROUPS_TOKEN_PATH,
  SYNC_PROFILE_FIELDS,
  ACCESS_TOKEN_PATH,
  BOOKING_ADMIN,
  BOOKING_GROUP,
  EDITORS_GROUP,
  REQUIRED_GROUPS,
  INSTRUMENT_REPOSITORY_ADMIN,
  INSTRUMENT_REPOSITORY_USER,
  INSTRUMENT_REPOSITORY_VIEWER,
  REPO_CONFIG,
  displayColumns,
  DEFAULT_COLUMNS
} from "../../constants";
import {
  CREATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE,
  UPDATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE_PERSONAL_FIELDS
} from "../../gql/logBooksapi/mutations";
import {
  INSTRUMENT_BY_SITE,
  LOGBOOK_GET_LOGGED_USER_DETAILS,
  LIST_DIGITAL_LAB_LOGBOOK_USER_PROFILES,
  GET_DIGITAL_LAB_LOGBOOK_INSTRUMENT,
  GET_DIGITAL_LAB_LOGBOOK_INSTRUMENT_BY_CLUSTERID_AND_SITE,
  LIST_IR_GROUP_ADMIN_MAPPINGS,
  BOOKING_GET_USER_CIDM_GROUPS
} from "../../gql/logBooksapi/queries";
import { loadEquipmentModels } from "./redux/actions";
import { loadEquipmentSignee } from "./redux/actions";
import { getAllData } from "../../utils/helpers/fetching";
import { isEqual, pick, sortBy, uniq } from "underscore";
import { logSheetLoadItemSpecifics } from "./log-sheet/log-sheet-form/log-sheet-load-item-specifics";
import { runLogsLoadItemSpecifics } from "./run-logs/run-logs-form/run-logs-load-item-specifics";
import { Notify } from "@digitallab/grid-common-frontend-components";
import { CircularProgress, Typography } from "@mui/material";
import { OwcButton } from "@one/react";
import { getMegaClusterIds } from "./loadLogBookInfo";
import { loadMegaClusterIds } from "./log-book-item-form-dialog/redux/actions";
import { getAppSyncBookingClient } from "../../booking_client";

export const FullScreenCentered = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin: 0;
  width: 100vw;
  height: 100vh;
`;

const CONDITIONAL_REQUEST_FAILED_ERROR = "GraphQL error: The conditional request failed";
const EMAIL_CHANGED_MESSAGE =
  "Your email has changed, please wait a minute and login again for the change to take effect";

const checkAccesGroupInArray = (access_groups, array) => {
  return access_groups.some((group) => array.includes(group));
};
class SiteError extends Error {
  constructor(message) {
    super(message);
    this.name = "SiteError";
  }
}

const stripPhoneNumber = (phoneNumber) => phoneNumber?.replace(/[^+\d]/gi, "") ?? "";

const normalizeEmail = (email) => (email || "").trim().toLowerCase();

const parseIdentitiesClaim = (identitiesClaim) => {
  if (Array.isArray(identitiesClaim)) {
    return identitiesClaim;
  }
  if (typeof identitiesClaim !== "string" || identitiesClaim.trim() === "") {
    return [];
  }
  try {
    const parsed = JSON.parse(identitiesClaim);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const isSsoUser = (currentAuthenticatedUser) => {
  const identitiesClaim = get(currentAuthenticatedUser, "signInUserSession.idToken.payload.identities");
  return parseIdentitiesClaim(identitiesClaim).length > 0;
};

export const hasEmailChanged = ({ cognitoEmail, profileEmail }) => {
  const normalizedCognitoEmail = normalizeEmail(cognitoEmail);
  const normalizedProfileEmail = normalizeEmail(profileEmail);
  return Boolean(normalizedCognitoEmail && normalizedProfileEmail && normalizedCognitoEmail !== normalizedProfileEmail);
};

const isConditionalRequestFailedError = (error) => {
  const message = error?.message || "";
  if (message.includes(CONDITIONAL_REQUEST_FAILED_ERROR) || message.includes("ConditionalCheckFailedException")) {
    return true;
  }
  const graphqlErrors = error?.graphQLErrors || [];
  return graphqlErrors.some((graphQLError) => {
    const graphQlMessage = graphQLError?.message || "";
    const errorType =
      graphQLError?.errorType ||
      graphQLError?.extensions?.errorType ||
      graphQLError?.extensions?.exception?.errorType ||
      graphQLError?.originalError?.errorType;
    return (
      graphQlMessage.includes(CONDITIONAL_REQUEST_FAILED_ERROR) ||
      graphQlMessage.includes("ConditionalCheckFailedException") ||
      errorType === "ConditionalCheckFailedException"
    );
  });
};

const getIrRole = (access_groups) => {
  if (access_groups.includes(INSTRUMENT_REPOSITORY_ADMIN)) {
    return "Admin";
  }
  if (access_groups.includes(INSTRUMENT_REPOSITORY_USER)) {
    return "User";
  }
  if (access_groups.includes(INSTRUMENT_REPOSITORY_VIEWER)) {
    return "Viewer";
  }
  return "Viewer";
};

const checkRoleAndGetUserEmail = async () => {
  const currentAuthenticatedUser = await Auth.currentAuthenticatedUser();
  const access_groups = get(currentAuthenticatedUser, GROUPS_TOKEN_PATH);
  if (!Array.isArray(access_groups) || !checkAccesGroupInArray(access_groups, REQUIRED_GROUPS)) {
    throw new Error("You do not have permission to access.");
  }
  const canEdit = checkAccesGroupInArray(access_groups, EDITORS_GROUP);
  const canBook = checkAccesGroupInArray(access_groups, BOOKING_GROUP);
  const isBookingAdmin = access_groups.includes(BOOKING_ADMIN);
  const irRole = getIrRole(access_groups);
  const email = get(currentAuthenticatedUser, "attributes.email");
  const syncFields = pick(get(currentAuthenticatedUser, ACCESS_TOKEN_PATH), Object.values(SYNC_PROFILE_FIELDS));
  if (!syncFields[SYNC_PROFILE_FIELDS.name]) {
    syncFields[SYNC_PROFILE_FIELDS.name] = email?.split("@")?.[0] || "";
  }
  if (syncFields?.[SYNC_PROFILE_FIELDS.phone]) {
    syncFields[SYNC_PROFILE_FIELDS.phone] = stripPhoneNumber(syncFields?.[SYNC_PROFILE_FIELDS.phone]);
  }
  return { email, canEdit, canBook, isBookingAdmin, irRole, isSso: isSsoUser(currentAuthenticatedUser), ...syncFields };
};

export const updateProfileFields = async ({ client, cognitoEmail, user, ...syncFields }) => {
  const syncKeys = Object.keys(SYNC_PROFILE_FIELDS);
  const profileObj = syncKeys.reduce((acc, currKey) => {
    const val = syncFields?.[SYNC_PROFILE_FIELDS?.[currKey]];
    return val ? { ...acc, [currKey]: val } : acc;
  }, {});
  if (!user) {
    return profileObj;
  }
  const email = user?.email || cognitoEmail;
  const shouldUpdate = !isEqual(pick(user, syncKeys), {
    ...profileObj,
    site: profileObj?.site || user.site
  });
  if (!shouldUpdate) {
    return user;
  }
  const {
    data: { updateDigitalLabLogbookUserProfile: updatedUser }
  } = await client.mutate({
    mutation: UPDATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE_PERSONAL_FIELDS,
    fetchPolicy: "no-cache",
    variables: {
      id: user?.id,
      email,
      ...profileObj,
      site: profileObj?.site || user?.site
    }
  });
  return updatedUser;
};

export const getUserDetails = async ({ client, email, isSso, ...syncFields }) => {
  const cognitoEmail = email;
  const result = await client.query({
    query: LOGBOOK_GET_LOGGED_USER_DETAILS,
    fetchPolicy: "no-cache"
  });

  const user = get(result, "data.getLoggedInUserDetails.items[0]");
  const resolvedEmail = user?.email || cognitoEmail;

  if (isSso && hasEmailChanged({ cognitoEmail, profileEmail: user?.email })) {
    throw new SiteError(EMAIL_CHANGED_MESSAGE);
  }

  if (!syncFields?.[SYNC_PROFILE_FIELDS.site] && !user?.site) {
    throw new SiteError("You do not have site location assigned. Go to the Landing Page to configure this.");
  }
  if (!syncFields?.[SYNC_PROFILE_FIELDS.site]) {
    Notify({
      type: "warning",
      icon: "alarm",
      title: "",
      text: "Your location has been changed. Please check your settings at the Landing Page."
    });
  }

  const profileFieldsOrUpdatedUser = await updateProfileFields({
    client,
    user,
    cognitoEmail: resolvedEmail,
    ...syncFields
  });

  if (profileFieldsOrUpdatedUser?.id) {
    return { user: profileFieldsOrUpdatedUser };
  }

  const {
    data: { createDigitalLabLogbookUserProfile }
  } = await client.mutate({
    mutation: CREATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE,
    fetchPolicy: "no-cache",
    variables: {
      email: resolvedEmail,
      ...profileFieldsOrUpdatedUser
    }
  });

  return {
    user: createDigitalLabLogbookUserProfile
  };
};

export const getInstrumentByClusterIdAndSite = async (clusterId, siteName, client) => {
  if (siteName) {
    try {
      const result = await client.query({
        query: GET_DIGITAL_LAB_LOGBOOK_INSTRUMENT_BY_CLUSTERID_AND_SITE,
        fetchPolicy: "no-cache",
        variables: {
          siteName: siteName,
          clusterId: clusterId
        }
      });
      if (result?.data?.instrumentBySiteAndClusterId) {
        return result?.data?.instrumentBySiteAndClusterId;
      }
    } catch (err) {
      console.log("error", err.message);
    }
  }
};

export const getEquipDetailInfo = async (id, client) => {
  if (id !== undefined) {
    try {
      const result = await client.query({
        query: GET_DIGITAL_LAB_LOGBOOK_INSTRUMENT,
        fetchPolicy: "no-cache",
        variables: {
          inventoryId: id
        }
      });
      if (result?.data?.getDigitalLabLogbookInstrument) {
        return result?.data?.getDigitalLabLogbookInstrument;
      }
    } catch (err) {}
  }
};

const LoadLogBooksInfo = ({
  children,
  client,
  loadUserInfo,
  loadEquipmentModels,
  loadEquipmentSignee,
  loadMegaClusterIds
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const dispatch = useDispatch();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      let isSso = false;
      try {
        const userAccess = await checkRoleAndGetUserEmail();
        const { email, canEdit, canBook, isBookingAdmin, irRole, isSso: isSsoUserFromClaims, ...syncFields } = userAccess;
        isSso = isSsoUserFromClaims;
        const { user } = await getUserDetails({ client, email, isSso, ...syncFields });

        if (!user?.lastDisplayColumns) {
          // DEFAULT_COLUMNS() is now derived from allTableColumn() and already has this exact
          // shape, so take it whole rather than rebuilding the same split by hand.
          user.lastDisplayColumns = DEFAULT_COLUMNS();
        }

        if (user?.lastDisplayColumns) {
          const profileEmail = user?.email || email;
          const lastDisplayColumns =
            typeof user?.lastDisplayColumns === "string"
              ? JSON.parse(user?.lastDisplayColumns)
              : user?.lastDisplayColumns;
          if (!lastDisplayColumns.hasOwnProperty(displayColumns.EQUIPMENT_LIST)) {
            user.lastDisplayColumns = {
              equipmentList: user?.lastDisplayColumns
            };
            client.mutate({
              mutation: UPDATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE_PERSONAL_FIELDS,
              variables: {
                id: user?.id,
                email: profileEmail,
                lastDisplayColumns: JSON.stringify(user?.lastDisplayColumns)
              },
              fetchPolicy: "no-cache"
            });
          }
        }
        const [{ items: equipmentModel }, { items: userProfiles }, megaClusterIds, , , { items: allIrGroups }, cidmGroupsResult] =
          await Promise.all([
            getAllData({
              client,
              query: INSTRUMENT_BY_SITE,
              variables: {
                siteName: user.site,
                limit: 1000
              },
              dataPath: ["data", "instrumentBySite"],
              drillData: true
            }),
            getAllData({
              client,
              query: LIST_DIGITAL_LAB_LOGBOOK_USER_PROFILES,
              variables: {
                limit: 1000
              },
              dataPath: ["data", "listDigitalLabLogbookUserProfiles"],
              drillData: true
            }),
            getMegaClusterIds(),
            logSheetLoadItemSpecifics({
              client,
              dispatch,
              site: user?.site
            }),
            runLogsLoadItemSpecifics({
              client,
              dispatch,
              site: user?.site
            }),
            getAllData({
              client,
              query: LIST_IR_GROUP_ADMIN_MAPPINGS,
              variables: { limit: 1000 },
              dataPath: ["data", "listIRGroupAdminMappings"],
              drillData: true,
              awsAppSyncConfig: REPO_CONFIG
            }),
            getAppSyncBookingClient().query({
              query: BOOKING_GET_USER_CIDM_GROUPS,
              fetchPolicy: "no-cache"
            }).then((res) => res?.data?.getLoggedInUserDetails?.items?.[0]?.cidmGroups || [])
              .catch(() => [])
          ]);

        const irGroups = (allIrGroups || []).filter(
          (group) =>
            group.userIds?.some((u) => u.userId === user.user) ||
            group.reviewers?.some((r) => r.userId === user.user)
        );

        const cidmGroups = Array.isArray(cidmGroupsResult) ? cidmGroupsResult : [];

        loadUserInfo({
          ...user,
          canEdit,
          canBook,
          isBookingAdmin,
          cidmGroups,
          irRole,
          irGroups
        });

        loadEquipmentModels(
          sortBy(uniq(equipmentModel.map((type) => type.equipmentModel)), (v) => v?.toLowerCase()).filter((v) =>
            Boolean(v)
          )
        );
        loadMegaClusterIds(megaClusterIds);
        let list = userProfiles.map((item) => ({
          ...item,
          key: item?.email,
          value: item?.name
        }));
        list = sortBy(list, "value").filter((v) => v.value !== "");
        loadEquipmentSignee(uniq(list, "value"));
      } catch (err) {
        const resolvedError = isSso && isConditionalRequestFailedError(err)
          ? new SiteError(EMAIL_CHANGED_MESSAGE)
          : err || new Error("Unexpected error has occurred");
        console.warn(resolvedError);
        setError(resolvedError);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadUserInfo, loadEquipmentModels, client, retry, dispatch, loadEquipmentSignee]);
  if (loading) {
    return (
      <FullScreenCentered>
        <CircularProgress data-testid="loading" size={80} />
      </FullScreenCentered>
    );
  }
  if (error !== null) {
    return (
      <FullScreenCentered>
        <>
          <Typography variant="h6" data-testid="message-error" gutterBottom>
            {error?.message ? error?.message : "Unexpected error has occurred"}
          </Typography>
          {error?.name !== "SiteError" && (
            <OwcButton
              {...react18WorkaroundProps}
              variant="error"
              onClick={() => setRetry(retry + 1)}
              data-testid="try-again-button"
            >
              Try again
            </OwcButton>
          )}
          <OwcButton
            {...react18WorkaroundProps}
            data-testid="signout-button"
            variant="secondary"
            style={{ marginTop: ".7rem" }}
            onClick={() => signOut()}
          >
            Sign out
          </OwcButton>
        </>
      </FullScreenCentered>
    );
  }
  return children;
};

export default compose(
  connect(null, {
    loadUserInfo,
    loadEquipmentModels,
    loadEquipmentSignee,
    loadMegaClusterIds
  }),
  withApollo
)(LoadLogBooksInfo);
